// Remote support («Soporte remoto») — the wire, as shared/REMOTE-SUPPORT.md defines it. Pure: no DOM, no
// WebRTC, no network, so every shape is unit-tested and the Windows supporthost bundle, the web client and
// the tests all read the same decoder. Twins: the C# / Swift / Kotlin protocol files of the other slices.
//
//  §2   the plugin's session record + its two WebSocket events
//  §3.1 the signal payloads (offer / answer / candidate / ready) relayed by `POST /sessions/{id}/signal`
//  §4   the `rs` data channel (helper → requester input, requester → helper hello/control/bye)
//
// Every decoder answers null for anything malformed — a peer on another version must never crash us — and
// clamps numbers into their documented range, so a hostile helper cannot push a pointer off the display.

export const PLUGIN_ID = 'com.satoritech.remotesupport';
export const API_BASE = `/plugins/${PLUGIN_ID}/api/v1`;
export const EVENT_PREFIX = `custom_${PLUGIN_ID}_`;
export const EVENT_SESSION = `${EVENT_PREFIX}session`;
export const EVENT_SIGNAL = `${EVENT_PREFIX}signal`;
/** The DM post the plugin writes and keeps updating (§2). */
export const POST_TYPE = 'custom_remote_support';
/** The data channel's label (ordered, reliable). */
export const DATA_CHANNEL_LABEL = 'rs';
export const PROTOCOL_VERSION = 1;
/** A requested session rings for this long, then the plugin expires it (§1.2). */
export const RING_MS = 60_000;
/** The link may be down this long before both ends give up (§1.7). */
export const LINK_DROP_MS = 20_000;
/** The helper sends at most this many pointer moves per second (§4). */
export const MAX_MOVES_PER_SECOND = 60;
/** Local physical input pauses remote input this long (§5). */
export const LOCAL_INPUT_PAUSE_MS = 1_000;

// ───────────────────────────────── §2 session record ─────────────────────────────────

export type SessionState = 'requested' | 'connecting' | 'active' | 'ended' | 'declined' | 'cancelled' | 'expired';
const STATES: readonly SessionState[] = ['requested', 'connecting', 'active', 'ended', 'declined', 'cancelled', 'expired'];

export interface SupportSession {
  id: string;
  requesterId: string;
  helperId: string;
  channelId: string;
  state: SessionState;
  control: boolean;
  createdAt: number;
  updatedAt: number;
  endedReason: string;
  postId: string;
}

const str = (v: unknown): string => (typeof v === 'string' ? v : '');
const num = (v: unknown): number => (typeof v === 'number' && Number.isFinite(v) ? v : 0);
const isObject = (v: unknown): v is Record<string, unknown> => !!v && typeof v === 'object' && !Array.isArray(v);

/** The plugin's JSON record (snake_case) → ours. Null without an id, members or a known state. */
export function parseSession(raw: unknown): SupportSession | null {
  if (!isObject(raw)) return null;
  const id = str(raw.id);
  const requesterId = str(raw.requester_id);
  const helperId = str(raw.helper_id);
  const state = str(raw.state) as SessionState;
  if (!id || !requesterId || !helperId || !STATES.includes(state)) return null;
  return {
    id,
    requesterId,
    helperId,
    channelId: str(raw.channel_id),
    state,
    control: raw.control === true,
    createdAt: num(raw.created_at),
    updatedAt: num(raw.updated_at),
    endedReason: str(raw.ended_reason),
    postId: str(raw.post_id),
  };
}

/** States a session never leaves. */
export function isFinished(state: SessionState): boolean {
  return state === 'ended' || state === 'declined' || state === 'cancelled' || state === 'expired';
}

/** States in which the two peers exchange signals (§2 `signal`). */
export function isLive(state: SessionState): boolean {
  return state === 'connecting' || state === 'active';
}

export type SupportEvent =
  | { kind: 'session'; session: SupportSession }
  | { kind: 'signal'; sessionId: string; fromUserId: string; payload: SignalPayload };

/** A main-socket envelope's `event` + `data` → a remote-support event, or null (not ours / malformed). */
export function parseEvent(event: string | undefined, data: unknown): SupportEvent | null {
  if (!event || !event.startsWith(EVENT_PREFIX) || !isObject(data)) return null;
  if (event === EVENT_SESSION) {
    // The server-side WebSocket payload may carry the record as an object or as a JSON string.
    const raw = typeof data.session === 'string' ? safeJson(data.session) : data.session;
    const session = parseSession(raw);
    return session ? { kind: 'session', session } : null;
  }
  if (event === EVENT_SIGNAL) {
    const sessionId = str(data.session_id);
    const raw = typeof data.payload === 'string' ? safeJson(data.payload) : data.payload;
    const payload = parseSignal(raw);
    if (!sessionId || !payload) return null;
    return { kind: 'signal', sessionId, fromUserId: str(data.from_user_id), payload };
  }
  return null;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

// ───────────────────────────────── §3.1 signal payloads ─────────────────────────────────

export type SignalPayload =
  | { type: 'offer'; sdp: string }
  | { type: 'answer'; sdp: string }
  | { type: 'candidate'; candidate: string; sdpMid: string; sdpMLineIndex: number }
  | { type: 'ready' };

/** The plugin refuses payloads above 64 KB; an SDP is ~5 KB, so a bigger one is a bug on our side. */
export const MAX_SIGNAL_BYTES = 64 * 1024;

export function parseSignal(raw: unknown): SignalPayload | null {
  if (!isObject(raw)) return null;
  switch (raw.type) {
    case 'offer':
    case 'answer': {
      const sdp = str(raw.sdp);
      return sdp ? { type: raw.type, sdp } : null;
    }
    case 'candidate': {
      const candidate = str(raw.candidate);
      if (!candidate) return null;
      const index = typeof raw.sdpMLineIndex === 'number' && Number.isInteger(raw.sdpMLineIndex) && raw.sdpMLineIndex >= 0 ? raw.sdpMLineIndex : 0;
      return { type: 'candidate', candidate, sdpMid: str(raw.sdpMid), sdpMLineIndex: index };
    }
    case 'ready':
      return { type: 'ready' };
    default:
      return null;
  }
}

// ───────────────────────────────── §4 data channel ─────────────────────────────────

export const Button = { left: 0, middle: 1, right: 2 } as const;
export const Mod = { shift: 1, ctrl: 2, alt: 4, meta: 8 } as const;

/** Helper → requester. */
export type HelperMessage =
  | { t: 'mm'; x: number; y: number }
  | { t: 'md' | 'mu'; b: number; x: number; y: number }
  | { t: 'wh'; dx: number; dy: number }
  | { t: 'kd' | 'ku'; code: string; key: string; m: number }
  | { t: 'txt'; s: string }
  | { t: 'want' };

export type RequesterOs = 'windows' | 'macos';

/** Requester → helper. */
export type RequesterMessage =
  | { t: 'hello'; v: number; os: RequesterOs; w: number; h: number; name: string; control: boolean }
  | { t: 'control'; granted: boolean }
  | { t: 'bye'; reason: string }
  /**
   * `elevated`: the requester could not inject (an elevated window on Windows). `view_only_no_permission`: the
   * requester's Mac has not granted Accessibility — it can only be watched (sticky; «Pedir control» is disabled).
   * Unknown codes are ignored.
   */
  | { t: 'notice'; code: NoticeCode };

export type NoticeCode = 'elevated' | 'view_only_no_permission';

/** A data-channel message larger than this is not ours (the biggest legitimate one is a `txt` paste). */
export const MAX_MESSAGE_CHARS = 16 * 1024;
/** `txt` is typed character by character on the requester: a cap keeps one message from typing for minutes. */
export const MAX_TEXT_CHARS = 4_000;
/** A wheel step beyond this is a broken (or hostile) peer, not a mouse. */
export const MAX_WHEEL_PX = 10_000;

const clamp = (v: number, lo: number, hi: number) => (v < lo ? lo : v > hi ? hi : v);
const unit = (v: unknown): number | null => (typeof v === 'number' && Number.isFinite(v) ? clamp(v, 0, 1) : null);
/** KeyboardEvent.code is ASCII letters/digits ("KeyA", "ArrowLeft", "F12", "Numpad0"). */
const CODE_RE = /^[A-Za-z][A-Za-z0-9]{0,31}$/;

export function encode(message: HelperMessage | RequesterMessage): string {
  return JSON.stringify(message);
}

/** What a REQUESTER reads off the channel. Null = ignore it. */
export function decodeHelperMessage(text: unknown): HelperMessage | null {
  if (typeof text !== 'string' || text.length > MAX_MESSAGE_CHARS) return null;
  const m = safeJson(text);
  if (!isObject(m)) return null;
  switch (m.t) {
    case 'mm': {
      const x = unit(m.x);
      const y = unit(m.y);
      return x === null || y === null ? null : { t: 'mm', x, y };
    }
    case 'md':
    case 'mu': {
      const x = unit(m.x);
      const y = unit(m.y);
      if (x === null || y === null) return null;
      if (m.b !== 0 && m.b !== 1 && m.b !== 2) return null;
      return { t: m.t, b: m.b, x, y };
    }
    case 'wh': {
      const dx = typeof m.dx === 'number' && Number.isFinite(m.dx) ? clamp(Math.round(m.dx), -MAX_WHEEL_PX, MAX_WHEEL_PX) : 0;
      const dy = typeof m.dy === 'number' && Number.isFinite(m.dy) ? clamp(Math.round(m.dy), -MAX_WHEEL_PX, MAX_WHEEL_PX) : 0;
      return dx === 0 && dy === 0 ? null : { t: 'wh', dx, dy };
    }
    case 'kd':
    case 'ku': {
      const code = str(m.code);
      if (!CODE_RE.test(code)) return null;
      const key = str(m.key).slice(0, 32);
      const mods = typeof m.m === 'number' && Number.isInteger(m.m) ? m.m & 15 : 0;
      return { t: m.t, code, key, m: mods };
    }
    case 'txt': {
      const s = str(m.s);
      return s ? { t: 'txt', s: s.slice(0, MAX_TEXT_CHARS) } : null;
    }
    case 'want':
      return { t: 'want' };
    default:
      return null;
  }
}

/** What a HELPER reads off the channel. Null = ignore it. */
export function decodeRequesterMessage(text: unknown): RequesterMessage | null {
  if (typeof text !== 'string' || text.length > MAX_MESSAGE_CHARS) return null;
  const m = safeJson(text);
  if (!isObject(m)) return null;
  switch (m.t) {
    case 'hello': {
      const os: RequesterOs = m.os === 'macos' ? 'macos' : 'windows';
      const w = Math.max(0, Math.round(num(m.w)));
      const h = Math.max(0, Math.round(num(m.h)));
      return { t: 'hello', v: Math.round(num(m.v)) || 1, os, w, h, name: str(m.name).slice(0, 120), control: m.control === true };
    }
    case 'control':
      return typeof m.granted === 'boolean' ? { t: 'control', granted: m.granted } : null;
    case 'bye':
      return { t: 'bye', reason: str(m.reason).slice(0, 200) };
    case 'notice':
      return m.code === 'elevated' || m.code === 'view_only_no_permission' ? { t: 'notice', code: m.code } : null;
    default:
      return null;
  }
}

/** The modifier bitmask of a keyboard / pointer event (§4 `m`). */
export function modifiers(e: { shiftKey?: boolean; ctrlKey?: boolean; altKey?: boolean; metaKey?: boolean }): number {
  return (e.shiftKey ? Mod.shift : 0) | (e.ctrlKey ? Mod.ctrl : 0) | (e.altKey ? Mod.alt : 0) | (e.metaKey ? Mod.meta : 0);
}

/** DOM WheelEvent deltas → pixels (§4: lines ×40, pages ×800). */
export function wheelPixels(deltaX: number, deltaY: number, deltaMode: number): { dx: number; dy: number } {
  const k = deltaMode === 1 ? 40 : deltaMode === 2 ? 800 : 1;
  const r = (v: number) => (Number.isFinite(v) ? clamp(Math.round(v * k), -MAX_WHEEL_PX, MAX_WHEEL_PX) : 0);
  return { dx: r(deltaX), dy: r(deltaY) };
}

const CTRL_ALT_SHIFT = Mod.ctrl | Mod.alt | Mod.shift;

/** Ctrl+Alt+Shift+S (Mac ⌃⌥⇧S): the requester's emergency stop. From the helper it is DROPPED (§5). */
export function isEmergencyStop(code: string, m: number): boolean {
  return code === 'KeyS' && (m & CTRL_ALT_SHIFT) === CTRL_ALT_SHIFT;
}

/** Ctrl+Alt+Shift+R (Mac ⌃⌥⇧R): the helper's own "stop sending keys" combo (§6). Never sent. */
export function isReleaseCombo(code: string, m: number): boolean {
  return code === 'KeyR' && (m & CTRL_ALT_SHIFT) === CTRL_ALT_SHIFT;
}

/** Ctrl+Alt+Del — the secure-attention sequence is never injected (§5). */
export function isSecureAttention(code: string, m: number): boolean {
  return (code === 'Delete' || code === 'NumpadDecimal') && (m & (Mod.ctrl | Mod.alt)) === (Mod.ctrl | Mod.alt);
}
